import OpenAI from 'openai';

export interface TranscriptionOptions {
  language?: string;
  prompt?: string;
  response_format?: 'json' | 'text' | 'srt' | 'verbose_json' | 'vtt';
  temperature?: number;
}

export interface TranscriptionResult {
  transcription: string;
  language?: string;
  confidence?: number;
  error?: string;
}

/**
 * Client-side transcription service using OpenAI Whisper API
 * Bypasses backend to avoid CORS issues when backend is not running
 */
export class TranscriptionService {
  private static instance: TranscriptionService | null = null;
  private openai: OpenAI | null = null;
  private initialized = false;

  private constructor() {}

  static getInstance(): TranscriptionService {
    if (!TranscriptionService.instance) {
      TranscriptionService.instance = new TranscriptionService();
    }
    return TranscriptionService.instance;
  }

  /**
   * Initialize the service with OpenAI API key
   */
  private async initialize(): Promise<void> {
    if (this.initialized) return;

    // Try both VITE_OPENAI_API_KEY and OPENAI_API_KEY for flexibility
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY || import.meta.env.OPENAI_API_KEY;
    if (!apiKey) {
      throw new Error('OpenAI API key not found. Please set VITE_OPENAI_API_KEY or OPENAI_API_KEY in your environment.');
    }

    this.openai = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true // Enable browser usage
    });

    this.initialized = true;
  }

  /**
   * Transcribe audio blob using OpenAI Whisper
   */
  async transcribeAudio(
    audioBlob: Blob,
    options: TranscriptionOptions = {}
  ): Promise<TranscriptionResult> {
    try {
      console.log('🎤 Initializing client-side transcription...');
      await this.initialize();

      if (!this.openai) {
        throw new Error('OpenAI client not initialized');
      }

      // Convert blob to File object (required by OpenAI API)
      const audioFile = new File([audioBlob], 'audio.webm', {
        type: audioBlob.type || 'audio/webm'
      });

      console.log('📤 Sending transcription request to OpenAI Whisper...', {
        size: audioBlob.size,
        type: audioBlob.type,
        duration: 'unknown'
      });

      const transcription = await this.openai.audio.transcriptions.create({
        file: audioFile,
        model: 'whisper-1',
        language: options.language || 'id', // Default to Indonesian
        prompt: options.prompt,
        response_format: options.response_format || 'text',
        temperature: options.temperature || 0
      });

      console.log('✅ Transcription successful:', transcription);

      return {
        transcription: typeof transcription === 'string' ? transcription : transcription.text,
        language: options.language
      };

    } catch (error) {
      console.error('❌ Transcription failed:', error);
      
      let errorMessage = 'Failed to transcribe audio';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        transcription: '',
        error: errorMessage
      };
    }
  }

  /**
   * Check if transcription is available
   */
  isAvailable(): boolean {
    return !!(import.meta.env.VITE_OPENAI_API_KEY || import.meta.env.OPENAI_API_KEY);
  }

  /**
   * Get supported audio formats
   */
  getSupportedFormats(): string[] {
    return [
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
      'audio/wav',
      'audio/ogg'
    ];
  }
}

// Export singleton instance
export const transcriptionService = TranscriptionService.getInstance();